/**
 * FHIR Medication Store - Zustand State Management
 * Manages FHIR medication state for the application
 */

import {
  fhirMedicationService,
  medicationDisplayHelpers,
} from "@/services/api/fhirMedicationService";
import type {
  FHIRMedicationStatement,
  FHIRMedicationStats,
  MedicationCreateRequest,
  MedicationDisplay,
  MedicationQueryParams,
  MedicationUpdateRequest,
} from "@/types/fhir";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { log } from "@/lib/log";
import { useI18nStore } from "@/stores/i18nStore";
import type { Translation } from "@/types/i18n";

/**
 * Fallback error text in the active language, read at call time so a
 * language switch is honoured without re-creating the store.
 */
const medxError = (key: keyof Translation & `medxErr${string}`): string =>
  useI18nStore.getState().t[key];

interface FHIRMedicationState {
  // State
  medications: MedicationDisplay[];
  activeMedications: MedicationDisplay[];
  statistics: FHIRMedicationStats | null;
  loading: boolean;
  error: string | null;
  selectedMedication: FHIRMedicationStatement | null;

  // Categories and Terminologies
  categories: Array<{ code: string; display: string; system: string }>;
  routes: Array<{ code: string; display: string; system: string }>;
  frequencies: Array<{ code: string; display: string; system: string }>;

  // Query State
  currentQuery: MedicationQueryParams | null;

  // Actions - Data Fetching
  fetchAllMedications: (params?: MedicationQueryParams) => Promise<void>;
  fetchActiveMedications: () => Promise<void>;
  fetchMedicationsByCategory: (category: string) => Promise<void>;
  fetchExpiringMedications: () => Promise<void>;
  fetchMedicationById: (id: string) => Promise<FHIRMedicationStatement | null>;
  fetchStatistics: () => Promise<void>;
  fetchTerminologies: () => Promise<void>;

  // Actions - CRUD Operations
  createMedication: (
    medicationData: unknown,
  ) => Promise<FHIRMedicationStatement | null>;
  // The declared parameter used to be widened to
  // `Record<string, unknown> | MedicationUpdateRequest`, which promised callers
  // they could pass any object - while the implementation and the service
  // underneath both require MedicationUpdateRequest. The implementation is the
  // truth here, so the interface follows it.
  updateMedication: (
    id: string,
    updateData: MedicationUpdateRequest,
  ) => Promise<FHIRMedicationStatement | null>;
  deleteMedication: (id: string) => Promise<boolean>;

  // Actions - Utility
  clearError: () => void;
  setSelectedMedication: (medication: FHIRMedicationStatement | null) => void;
  refreshData: () => Promise<void>;
}

/**
 * FHIR Medication Store
 * Centralized state management for FHIR medication data
 */
export const useFHIRMedicationStore = create<FHIRMedicationState>()(
  persist(
    (set, get) => ({
      // Initial State
      medications: [],
      activeMedications: [],
      statistics: null,
      loading: false,
      error: null,
      selectedMedication: null,
      categories: [],
      routes: [],
      frequencies: [],
      currentQuery: null,

      // Fetch All Medications
      fetchAllMedications: async (params?: MedicationQueryParams) => {
        set({ loading: true, error: null, currentQuery: params || null });

        try {
          const response =
            await fhirMedicationService.getAllMedications(params);

          // Backend returns the Bundle directly in response.data (not in response.data.bundle || response.data)
          if (response.success && response.data) {
            const bundle =
              response.data.bundle || response.data || response.data; // Support both structures
            const medications =
              medicationDisplayHelpers.bundleToDisplayArray(bundle);
            set({ medications, loading: false });
          } else {
            set({ medications: [], loading: false });
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error ? error.message : medxError("medxErrLoad");
          set({ error: errorMessage, loading: false, medications: [] });
        }
      },

      // Fetch Active Medications Only
      fetchActiveMedications: async () => {
        set({ loading: true, error: null });

        try {
          const response = await fhirMedicationService.getActiveMedications();

          if (response.success && response.data) {
            const bundle =
              response.data.bundle || response.data || response.data;
            const activeMedications =
              medicationDisplayHelpers.bundleToDisplayArray(bundle);
            set({ activeMedications, loading: false });
          } else {
            set({ activeMedications: [], loading: false });
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : medxError("medxErrLoadActive");
          set({ error: errorMessage, loading: false, activeMedications: [] });
        }
      },

      // Fetch Medications by Category
      fetchMedicationsByCategory: async (category: string) => {
        set({ loading: true, error: null });

        try {
          const response =
            await fhirMedicationService.getMedicationsByCategory(category);

          if (response.success && response.data) {
            const bundle = response.data.bundle || response.data;
            const medications =
              medicationDisplayHelpers.bundleToDisplayArray(bundle);
            set({ medications, loading: false });
          } else {
            set({ medications: [], loading: false });
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : medxError("medxErrLoadByCategory");
          set({ error: errorMessage, loading: false, medications: [] });
        }
      },

      // Fetch Expiring Medications
      fetchExpiringMedications: async () => {
        set({ loading: true, error: null });

        try {
          const response = await fhirMedicationService.getExpiringMedications();

          if (response.success && response.data) {
            const bundle = response.data.bundle || response.data;
            const medications =
              medicationDisplayHelpers.bundleToDisplayArray(bundle);
            set({ medications, loading: false });
          } else {
            set({ medications: [], loading: false });
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : medxError("medxErrLoadExpiring");
          set({ error: errorMessage, loading: false, medications: [] });
        }
      },

      // Fetch Single Medication by ID
      fetchMedicationById: async (
        id: string,
      ): Promise<FHIRMedicationStatement | null> => {
        set({ loading: true, error: null });

        try {
          const response = await fhirMedicationService.getMedicationById(id);

          if (response.success && response.data) {
            set({ selectedMedication: response.data, loading: false });
            return response.data;
          } else {
            set({ selectedMedication: null, loading: false });
            return null;
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : medxError("medxErrLoadOne");
          set({
            error: errorMessage,
            loading: false,
            selectedMedication: null,
          });
          return null;
        }
      },

      // Fetch Statistics
      fetchStatistics: async () => {
        try {
          const response = await fhirMedicationService.getStatistics();

          if (response.success && response.data) {
            set({ statistics: response.data });
          } else {
            set({ statistics: null });
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : medxError("medxErrLoadStats");
          set({ error: errorMessage, statistics: null });
        }
      },

      // Fetch Terminologies (Categories, Routes, Frequencies)
      fetchTerminologies: async () => {
        try {
          const response = await fhirMedicationService.getTerminologies();

          if (response.success && response.data) {
            set({
              categories: response.data.categories,
              routes: response.data.routes,
              frequencies: response.data.frequencies,
            });
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : medxError("medxErrLoadTerminologies");
          set({ error: errorMessage });
        }
      },

      // Create New Medication
      createMedication: async (
        medicationData: unknown,
      ): Promise<FHIRMedicationStatement | null> => {
        set({ loading: true, error: null });

        try {
          const response = await fhirMedicationService.createMedication(
            medicationData as MedicationCreateRequest,
          );

          if (response.success && response.data) {
            // Refresh medications list
            await get().fetchAllMedications(get().currentQuery || undefined);
            await get().fetchActiveMedications();
            await get().fetchStatistics();

            set({ loading: false });
            return response.data;
          } else {
            set({
              loading: false,
              error: response.error || medxError("medxErrCreate"),
            });
            return null;
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error ? error.message : medxError("medxErrCreate");
          set({ error: errorMessage, loading: false });
          return null;
        }
      },

      // Update Existing Medication
      updateMedication: async (
        id: string,
        updateData: MedicationUpdateRequest,
      ): Promise<FHIRMedicationStatement | null> => {
        set({ loading: true, error: null });

        try {
          const response = await fhirMedicationService.updateMedication(
            id,
            updateData,
          );

          if (response.success && response.data) {
            // Refresh medications list
            await get().fetchAllMedications(get().currentQuery || undefined);
            await get().fetchActiveMedications();
            await get().fetchStatistics();

            set({ loading: false, selectedMedication: response.data });
            return response.data;
          } else {
            set({
              loading: false,
              error: response.error || medxError("medxErrUpdate"),
            });
            return null;
          }
        } catch (error: any) {
          const errorMessage =
            error instanceof Error ? error.message : medxError("medxErrUpdate");
          set({ error: errorMessage, loading: false });
          return null;
        }
      },

      // Delete Medication
      deleteMedication: async (id: string): Promise<boolean> => {
        log.debug("🏪 STORE: deleteMedication called for ID:", id);
        const stateBefore = get().medications.length;
        log.debug("🏪 STORE: Medications count before:", stateBefore);

        set({ loading: true, error: null });

        try {
          log.debug("🏪 STORE: Calling backend API...");
          const response = await fhirMedicationService.deleteMedication(id);
          log.debug("🏪 STORE: Backend response:", response);

          if (response.success) {
            // IMMEDIATE UPDATE: Remove from local state first for instant UI update
            const currentMedications = get().medications;
            const currentActiveMedications = get().activeMedications;

            log.debug("🏪 STORE: Filtering out medication from local state...");
            log.debug(
              "🏪 STORE: Before filter - medications:",
              currentMedications.length,
            );

            const filteredMedications = currentMedications.filter(
              (med) => med.id !== id,
            );
            const filteredActiveMedications = currentActiveMedications.filter(
              (med) => med.id !== id,
            );

            log.debug(
              "🏪 STORE: After filter - medications:",
              filteredMedications.length,
            );

            set({
              medications: filteredMedications,
              activeMedications: filteredActiveMedications,
              loading: false,
              selectedMedication: null,
            });

            log.debug(
              "🏪 STORE: State updated, new count:",
              get().medications.length,
            );
            log.debug("🏪 STORE: Starting background refresh...");

            // BACKGROUND REFRESH: Fetch fresh data from backend to ensure consistency
            get().fetchAllMedications(get().currentQuery || undefined);
            get().fetchActiveMedications();
            get().fetchStatistics();

            return true;
          } else {
            console.error(
              "🏪 STORE: Delete failed - backend returned success:false",
            );
            set({
              loading: false,
              error: response.error || medxError("medxErrDelete"),
            });
            return false;
          }
        } catch (error: any) {
          console.error("🏪 STORE: Delete error:", error);
          const errorMessage =
            error instanceof Error ? error.message : medxError("medxErrDelete");
          set({ error: errorMessage, loading: false });
          return false;
        }
      },

      // Clear Error
      clearError: () => set({ error: null }),

      // Set Selected Medication
      setSelectedMedication: (medication: FHIRMedicationStatement | null) => {
        set({ selectedMedication: medication });
      },

      // Refresh All Data
      refreshData: async () => {
        const { currentQuery } = get();
        await Promise.all([
          get().fetchAllMedications(currentQuery || undefined),
          get().fetchActiveMedications(),
          get().fetchStatistics(),
          get().fetchTerminologies(),
        ]);
      },
    }),
    {
      name: "fhir-medication-storage",
      partialize: (state) => ({
        // Persist only non-sensitive, cacheable data
        categories: state.categories,
        routes: state.routes,
        frequencies: state.frequencies,
      }),
    },
  ),
);

export default useFHIRMedicationStore;
